/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import InputAdornment from "@mui/material/InputAdornment";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import MDCheckbox from "components/MDCheckbox";
import MDSelect from "components/MDSelect";
import TableHeader from "components/Table/TableHeader";
import MDTypography from "components/MDTypography";
import TabPanel from "components/TabPanel/TabPanel";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";
import ProductUpload from "./ProductUpload";

const initAttr = {
  id: "",
  sku: "",
  barcode: "",
  cat_id: "",
  image_id: "",
  unit_id: "",
  image: "",
  name: "",
  value: "",
  vat: "",
  cost: "",
  price: "",
  enable: "Y",
  created_at: "",
  updated_at: "",
};

const initFind = {
  search: "",
  order_by: "tb.sku",
  order_sort: "asc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function Product() {
  const mod = "rawmaterial";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [cboCat, setCboCat] = useState([]);
  const [cboUnit, setCboUnit] = useState([]);
  const [tab, setTab] = useState(0);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: "tb.image_id", label: t(`attr.image`), align: "center" },
    { id: "tb.sku", label: "SKU", align: "left" },
    { id: "tb.name", label: "ชื่อสินค้า", align: "left" },
    { id: "tb.cat_id", label: "หมวดหมู่", align: "left" },
    { id: "tb.unit_id", label: "หน่วยนับ", align: "left" },
    { id: "tb.cost", label: "ราคาต้นทุน", align: "right" },
    { id: "tb.price", label: "ราคาขาย", align: "right" },
    { id: "tb.enable", label: t(`attr.enable`), align: "center" },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === "ok") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const load = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);

    FetchData("GET", `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr({
          ...result.data,
          user_pass: "",
          user_pass_again: "",
          tel: Func.maskTel(result.data.tel),
        });

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.cat_id, 1)) return;
    if (!Func.verifyLength(attr.sku, 1)) return;
    if (!Func.verifyLength(attr.name, 1)) return;
    if (!Func.verifyLength(attr.unit_id, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData("PUT", `/${mod}`, app.token, {
      cat_id: attr.cat_id,
      sku: attr.sku,
      barcode: attr.barcode === "" ? attr.sku : attr.barcode,
      product_type: "RM",
      name: attr.name,
      value: attr.price,
      cost: attr.cost,
      price: attr.price,
      unit_id: attr.unit_id,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        view(find);
        showNotify(t(`alert.addcomplete`), "success");
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.cat_id, 1)) return;
    if (!Func.verifyLength(attr.sku, 1)) return;
    if (!Func.verifyLength(attr.name, 1)) return;
    if (!Func.verifyLength(attr.unit_id, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);

    FetchData("PATCH", `/${mod}/${attr.id}`, app.token, {
      cat_id: attr.cat_id,
      sku: attr.sku,
      barcode: attr.barcode === "" ? attr.sku : attr.barcode,
      name: attr.name,
      value: attr.price,
      cost: attr.cost,
      price: attr.price,
      unit_id: attr.unit_id,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        load();
        showNotify(t(`alert.editcomplete`), "success");
      } else {
        setConfirm(false);
        showNotify(t(`alert.${result.msg}`), "error");
      }
    });
  };

  const upload = (filedata) => {
    setLoading(true);

    FetchData("OPTIONS", `/${mod}/upload`, app.token, {
      id: id,
      filedata: filedata,
    }).then((result) => {
      load();
      setLoading(false);

      if (result.status === "ok") {
        showNotify(t(`alert.editcomplete`), "success");
      } else {
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const init = () => {
    FetchData("GET", `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr(initAttr);
        setCboCat(result.category);
        setCboUnit(result.unit);
        if (id === undefined) {
          view(find);
        } else {
          load();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const enableDisplay = (inp) => {
    if (inp === "Y") return <Icon color="info">verified</Icon>;
    return <Icon color="error">not_interested</Icon>;
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.image} variant="rounded" />;
  };

  const btnOpen = (item) => {
    return (
      <MDButton
        variant="text"
        color="info"
        size="small"
        href={`/${mod}/${item.id}`}
        sx={{ fontSize: "1rem" }}
      >
        {item.sku}
      </MDButton>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component="form"
              role="form"
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: "ALL", page: 1 });
              }}
            >
              <MDInput
                name="searchform"
                fullWidth
                value={find.search}
                type="search"
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{t("element.search")} :</InputAdornment>
                  ),
                  autoComplete: "off",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant="contained"
              color="dark"
              size="large"
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center">{imageDisplay(row)}</TableCell>
                      <TableCell align="left">{btnOpen(row)}</TableCell>
                      <TableCell align="left">{row.name}</TableCell>
                      <TableCell align="left">{row.cat_name}</TableCell>
                      <TableCell align="left">{row.unit_name}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.cost)}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.price)}</TableCell>
                      <TableCell align="center">{enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant="outlined"
                  color="secondary"
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAuthen = () => {
    return (
      <MDBox>
        <MDTypography variant="h4" mb={2}>
          Authen
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label="Username"
              onChange={(e) => setAttr({ ...attr, user_name: e.target.value })}
              value={attr.user_name}
              variant="standard"
              inputProps={{
                autoComplete: "off",
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="รหัสผ่าน"
              onChange={(e) => setAttr({ ...attr, user_pass: e.target.value })}
              value={attr.user_pass}
              variant="standard"
              type="password"
              inputProps={{
                autoComplete: "off",
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="รหัสผ่าน อีกครั้ง"
              onChange={(e) => setAttr({ ...attr, user_pass_again: e.target.value })}
              value={attr.user_pass_again}
              variant="standard"
              type="password"
              inputProps={{
                autoComplete: "off",
              }}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container mt={3}>
          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="outlined" color="success" onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDSelect
            label="หมวดหมู่ *"
            labelDefault={t(`element.please_select`)}
            data={cboCat}
            value={attr.cat_id}
            onChange={(e) => setAttr({ ...attr, cat_id: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label="หน่วยนับ *"
            labelDefault={t(`element.please_select`)}
            data={cboUnit}
            value={attr.unit_id}
            onChange={(e) => setAttr({ ...attr, unit_id: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Barcode"
            onChange={(e) => setAttr({ ...attr, barcode: e.target.value })}
            value={attr.barcode}
            variant="standard"
            fullWidth
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="SKU"
            onChange={(e) => setAttr({ ...attr, sku: e.target.value })}
            value={attr.sku}
            variant="standard"
            fullWidth
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="ชื่อสินค้า"
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant="standard"
            fullWidth
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="ราคาต้นทุน"
            onChange={(e) => setAttr({ ...attr, cost: e.target.value })}
            value={attr.cost}
            variant="standard"
            fullWidth
            type="number"
            inputProps={{
              autoComplete: "off",
              className: "text-center",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="ราคาขาย"
            onChange={(e) => setAttr({ ...attr, price: e.target.value })}
            value={attr.price}
            variant="standard"
            fullWidth
            type="number"
            inputProps={{
              autoComplete: "off",
              className: "text-center",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDCheckbox
            label="Enable"
            checked={attr.enable === "Y"}
            onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? "Y" : "N" })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth="sm" keepMounted aria-describedby="dialog-add" fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label="close"
            onClick={() => setModalAdd(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderImage = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <MDBox>
            <Paper sx={{ p: 2 }} variant="outlined">
              {attr.image === "" ? null : (
                <MDBox
                  component="img"
                  src={attr.image}
                  alt={attr.name}
                  width="100%"
                  height="100%"
                />
              )}
              <MDBox mt={2} display="flex" justifyContent="center">
                <ProductUpload uploadData={upload} />
              </MDBox>
            </Paper>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={12}>
            <NavLink to={`/${mod}`}>
              <MDButton variant="contained" color="dark">
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: "100%" }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="tabs-addedit">
                    <Tab label={t(`element.info`)} />
                    <Tab label="รูปภาพ" />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                      {renderInfo()}
                      {confirm ? (
                        <Grid container mt={3}>
                          <Grid item xs={6}>
                            <MDButton
                              variant="outlined"
                              color="error"
                              onClick={() => setConfirm(false)}
                            >
                              <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                            </MDButton>
                          </Grid>
                          <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <MDButton variant="gradient" color="success" onClick={edit}>
                              <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                            </MDButton>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container mt={3}>
                          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
                            <MDButton variant="outlined" color="success" onClick={editConfirm}>
                              <Icon>save</Icon>&nbsp;{t(`element.save`)}
                            </MDButton>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  {renderImage()}
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), t(`menu.product`)]} />
      {renderPage()}
      {renderAdd()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Product;
